import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Download } from "lucide-react";
import { AppShell, type AppShellContext } from "@/components/app/AppShell";
import {
  PaymentMethodPanel,
  type PaymentMethodPanelHandle,
} from "@/components/customer/PaymentMethodPanel";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type Subscription = {
  id: string;
  status: string;
  planName: string;
  priceUsd: number | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
};

type Invoice = {
  id: string;
  number: string | null;
  amountPaid: number;
  status: string | null;
  hostedInvoiceUrl: string | null;
  paidAt: string | null;
};

type BillingResp = { subscription: Subscription | null; invoices: Invoice[] };

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function StatusPill({
  status,
  cancelAtPeriodEnd,
}: {
  status: string;
  cancelAtPeriodEnd: boolean;
}) {
  if (cancelAtPeriodEnd && status === "active") {
    return (
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900">
        Canceling
      </span>
    );
  }
  if (status === "active" || status === "trialing") {
    return (
      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900">
        {status === "trialing" ? "Trialing" : "Active"}
      </span>
    );
  }
  if (status === "past_due" || status === "unpaid") {
    return (
      <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
        Past due
      </span>
    );
  }
  if (status === "canceled") {
    return (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
        Canceled
      </span>
    );
  }
  return (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
      {status}
    </span>
  );
}

export default function DashboardBilling() {
  return <AppShell>{(ctx) => <Inner ctx={ctx} />}</AppShell>;
}

function Inner({ ctx }: { ctx: AppShellContext }) {
  if (!ctx.isOwner) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Billing is owner-only
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Contact your account owner for billing details.
        </p>
        <Link
          to="/dashboard"
          className="mt-6 inline-flex rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          ← Back to dashboard
        </Link>
      </div>
    );
  }
  return <OwnerView />;
}

function OwnerView() {
  const [data, setData] = useState<BillingResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canceling, setCanceling] = useState(false);
  const paymentPanelRef = useRef<PaymentMethodPanelHandle>(null);

  async function load() {
    const res = await api<BillingResp>("/customer/billing");
    if (!res.ok) {
      setError(res.error ?? "Failed to load billing.");
      return;
    }
    setData(res.data ?? { subscription: null, invoices: [] });
  }

  useEffect(() => {
    load();
  }, []);

  async function onCancel() {
    if (!data?.subscription) return;
    const endLabel = formatDate(data.subscription.currentPeriodEnd);
    if (
      !window.confirm(
        `This will cancel at end of period — you'll keep access until ${endLabel}. Continue?`
      )
    )
      return;
    setCanceling(true);
    const res = await api("/customer/billing/cancel", { method: "POST" });
    setCanceling(false);
    if (!res.ok) {
      alert(res.error ?? "Could not cancel subscription.");
      return;
    }
    await load();
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-8">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Billing
          </h1>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            Plan, payment method, and invoice history.
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      )}

      {!data && !error && (
        <div className="mt-8 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {data && (
        <>
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Current plan
                </div>
                {data.subscription ? (
                  <>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                        {data.subscription.planName}
                      </span>
                      <StatusPill
                        status={data.subscription.status}
                        cancelAtPeriodEnd={data.subscription.cancelAtPeriodEnd}
                      />
                    </div>
                    <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      {data.subscription.priceUsd != null
                        ? `$${data.subscription.priceUsd}/mo`
                        : "—"}
                      {data.subscription.currentPeriodEnd && (
                        <>
                          <span className="mx-2 text-slate-300 dark:text-slate-600">·</span>
                          {data.subscription.cancelAtPeriodEnd
                            ? `Access until ${formatDate(data.subscription.currentPeriodEnd)}`
                            : `Next bill ${formatDate(data.subscription.currentPeriodEnd)}`}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
                      No active plan
                    </div>
                    <Link
                      to="/checkout/subscribe"
                      className="mt-3 inline-block text-sm font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                    >
                      Choose a plan →
                    </Link>
                  </>
                )}
              </div>
              {data.subscription &&
                !data.subscription.cancelAtPeriodEnd &&
                data.subscription.status !== "canceled" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onCancel}
                    disabled={canceling}
                    className="text-destructive hover:text-destructive"
                  >
                    {canceling && <Loader2 className="h-4 w-4 animate-spin" />}
                    {canceling ? "Canceling…" : "Cancel subscription"}
                  </Button>
                )}
            </div>
          </section>

          <section className="mt-6">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                Payment method
              </span>
              <button
                type="button"
                onClick={() => paymentPanelRef.current?.startAdd()}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              >
                Update
              </button>
            </div>
            <PaymentMethodPanel ref={paymentPanelRef} />
          </section>

          <section className="mt-6 rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-200 px-6 py-4 text-sm font-medium text-slate-900 dark:border-slate-800 dark:text-slate-100">
              Invoices
            </div>
            {data.invoices.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                No invoices yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                {data.invoices.map((inv) => (
                  <li
                    key={inv.id}
                    className="flex items-center justify-between gap-4 px-6 py-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {inv.number ?? inv.id.slice(0, 12)}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {formatDate(inv.paidAt)}
                      </div>
                    </div>
                    <div className="text-sm tabular-nums text-slate-700 dark:text-slate-300">
                      ${inv.amountPaid.toFixed(2)}
                    </div>
                    <div className="w-20 text-xs text-slate-500 dark:text-slate-400">
                      {inv.status ?? "—"}
                    </div>
                    {inv.hostedInvoiceUrl ? (
                      <a
                        href={inv.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                      >
                        <Download className="h-3.5 w-3.5" /> Download
                      </a>
                    ) : (
                      <span className="w-[88px]" />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
